import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RefreshCw, ScrollText } from "lucide-react";

const collections = [
  { value: "", label: "Semua" },
  { value: "bahan_baku", label: "Bahan Baku" },
  { value: "ops", label: "Operasional" },
  { value: "insentif", label: "Insentif Usaha" },
  { value: "lainnya", label: "Lainnya" },
  { value: "pendapatan", label: "Pendapatan" },
  { value: "members", label: "Anggota" },
  { value: "locked_months", label: "Kunci Bulan" },
];

const actions = [
  { value: "", label: "Semua" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "member_add", label: "Tambah Anggota" },
  { value: "member_remove", label: "Hapus Anggota" },
  { value: "lock", label: "Kunci" },
  { value: "unlock", label: "Buka Kunci" },
];

const actionTone = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-rose-50 text-rose-700 border-rose-200",
  lock: "bg-slate-100 text-slate-700 border-slate-200",
  unlock: "bg-slate-100 text-slate-700 border-slate-200",
  member_add: "bg-emerald-50 text-emerald-700 border-emerald-200",
  member_remove: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AuditLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState("");
  const [action, setAction] = useState("");
  const [limit, setLimit] = useState(200);

  useEffect(() => {
    if (user && !["super_admin", "admin"].includes(user.role)) return; // cabang can view /audit
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (collection) params.collection = collection;
      if (action) params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection, action, limit]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catatan Audit"
        subtitle="Rekam jejak semua perubahan data: siapa mengubah apa dan kapan."
        actions={
          <Button variant="outline" onClick={load} className="border-slate-300" data-testid="audit-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        }
      />

      <Card className="p-4 border-slate-200 flex flex-col md:flex-row gap-3 md:items-end">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Kategori</Label>
          <Select value={collection || "all"} onValueChange={(v) => setCollection(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="audit-collection"><SelectValue placeholder="Semua" /></SelectTrigger>
            <SelectContent className="bg-white">
              {collections.map((c) => (
                <SelectItem key={c.value || "all"} value={c.value || "all"}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Aksi</Label>
          <Select value={action || "all"} onValueChange={(v) => setAction(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="audit-action"><SelectValue placeholder="Semua" /></SelectTrigger>
            <SelectContent className="bg-white">
              {actions.map((a) => (
                <SelectItem key={a.value || "all"} value={a.value || "all"}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Limit</Label>
          <Input type="number" min="1" max="1000" value={limit} onChange={(e) => setLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 200)))}
                 className="w-32 h-9" data-testid="audit-limit" />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-40">Waktu</TableHead>
              <TableHead>Aktor</TableHead>
              <TableHead>Aksi</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>ID / Target</TableHead>
              <TableHead>Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="audit-table">
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Memuat...</TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12">
                <div className="flex flex-col items-center gap-2 text-slate-500">
                  <ScrollText className="h-8 w-8 text-slate-300" />
                  <div className="font-medium">Belum ada catatan audit</div>
                </div></TableCell></TableRow>
              : rows.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.actor}</TableCell>
                  <TableCell>
                    <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 border ${actionTone[r.action] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {r.action}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{r.collection}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-600 max-w-[180px] truncate" title={r.target_id}>{r.target_id}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-md">
                    <DetailPreview details={r.details} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function DetailPreview({ details }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-slate-300">—</span>;
  const summarize = (obj) => {
    if (!obj || typeof obj !== "object") return String(obj);
    return Object.entries(obj)
      .filter(([k]) => !["id", "created_at", "created_by", "attachments"].includes(k))
      .slice(0, 4)
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(", ");
  };
  if (details.before && details.after) {
    return (
      <div className="space-y-0.5">
        <div><span className="text-slate-400">sebelum:</span> {summarize(details.before)}</div>
        <div><span className="text-slate-400">sesudah:</span> {summarize(details.after)}</div>
      </div>
    );
  }
  if (details.after) return <span>{summarize(details.after)}</span>;
  if (details.before) return <span>{summarize(details.before)}</span>;
  return <span>{summarize(details)}</span>;
}
